import { inject, Injectable } from '@angular/core';
import { switchMap } from 'rxjs';
import { PSSRResponseService } from './api/pssr-response.service';
import { Dtos } from '#models';

interface SignatureRun {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

interface SignatureBlock {
  runs: SignatureRun[];
  align: 'left' | 'center' | 'right';
  fontSize: number;
  listMarker: string | null;
  indent: number;
}

@Injectable({
  providedIn: 'root',
})
export class SignService {
  private static readonly _signatureFont = '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif';

  private _responseService = inject(PSSRResponseService);

  loadResponseSignature(responseItemId: number, guid: string) {
    return this._responseService.getImageByGUID(responseItemId, guid);
  }

  loadParticipantSignature(participantGuid: string) {
    return this._responseService.getImageByGUID(0, participantGuid);
  }

  unsignParticipant$(deletePayload: Pick<Dtos.ResponseItemImage, 'GUID' | 'ParticipantGUID' | 'PSSRID' | 'Caption'>) {
    return this._responseService.uploadImage({
      ...deletePayload,
      Id: 0,
      Photo: '',
      ImageType: 1,
      ResponseItemGUID: null,
      ResponseItemID: 0,
      ResponseID: 0,
      ResponseGUID: null,
      IsDeleted: true,
    } as Dtos.ResponseItemImage);
  }
  signParticipant$(
    lines: string[],
    payload: {
      pssrUser: Pick<Dtos.PSSRUser, 'GUID' | 'PSSRID' | 'UserName'>;
      image: Pick<Dtos.ResponseItemImage, 'GUID' | 'CreateDate'>;
    }
  ) {
    const signatureUri = this.renderTextSignature(lines);
    return this._responseService.uploadImage({
      GUID: payload.image.GUID,
      Photo: signatureUri.replace('data:image/png;base64,', ''),
      CreateDate: payload.image.CreateDate,

      ParticipantGUID: payload.pssrUser.GUID,
      PSSRID: payload.pssrUser.PSSRID,
      Caption: payload.pssrUser.UserName,

      Id: 0,
      ImageType: 1,
      ResponseItemGUID: null,
      ResponseItemID: 0,
      ResponseID: 0,
      ResponseGUID: null,
      IsDeleted: false,
    });
  }

  // Renders the deterministic signature attestation text directly to a canvas,
  // avoiding a costly html2canvas DOM capture that blocks the main thread.
  // Width fits the longest line (clamped) so lines only wrap when genuinely too long.
  renderTextSignature(lines: string[]): string {
    const scale = 2;
    const padding = 20;
    const lineHeight = 20;
    const minWidth = 250;
    const maxWidth = 380;
    const font = 'bold 14px "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif';

    const measureCtx = document.createElement('canvas').getContext('2d');
    if (!measureCtx) {
      return '';
    }
    measureCtx.font = font;

    const longestLine = Math.max(0, ...lines.map(line => measureCtx.measureText(line).width));
    const width = Math.min(maxWidth, Math.max(minWidth, Math.ceil(longestLine) + padding * 2));
    const maxTextWidth = width - padding * 2;

    // Wrap only lines that still exceed the chosen width (e.g., a very long name).
    const wrapped: string[] = [];
    for (const line of lines) {
      let current = '';
      for (const word of line.split(' ')) {
        const candidate = current ? `${current} ${word}` : word;
        if (current && measureCtx.measureText(candidate).width > maxTextWidth) {
          wrapped.push(current);
          current = word;
        } else {
          current = candidate;
        }
      }
      wrapped.push(current);
    }

    const height = padding * 2 + wrapped.length * lineHeight;

    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return '';
    }
    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';
    ctx.font = font;
    wrapped.forEach((line, index) => ctx.fillText(line, padding, padding + index * lineHeight));

    return canvas.toDataURL('image/png');
  }

  signResponse$(
    content: { header: string; disclaimerHtml: string; footer: string },
    payload: {
      response: Dtos.PSSRResponse;
      signedBy: string;
    }
  ) {
    const signatureUri = this.renderRichSignature(content);
    return this._responseService.signResponse({
      ...payload,
      signatureDate: new Date(),
      signature: signatureUri.replace('data:image/png;base64,', ''),
    });
  }

  // Renders the response signature (bold header + rich-text disclaimer + footer)
  // directly to a canvas, preserving the disclaimer formatting produced by our
  // Quill toolbar (bold/italic/underline, headings, lists, alignment) without
  // the costly html2canvas DOM capture.
  renderRichSignature(content: { header: string; disclaimerHtml: string; footer: string }): string {
    const scale = 2;
    const padding = 20;
    const baseSize = 14;
    const markerGap = 18;
    const minWidth = 250;
    const maxWidth = 380;

    const blocks: SignatureBlock[] = [
      {
        runs: [{ text: content.header, bold: true, italic: false, underline: false }],
        align: 'left',
        fontSize: baseSize,
        listMarker: null,
        indent: 0,
      },
      ...this._parseDisclaimerBlocks(content.disclaimerHtml, baseSize),
      {
        runs: [{ text: content.footer, bold: false, italic: false, underline: false }],
        align: 'left',
        fontSize: baseSize,
        listMarker: null,
        indent: 0,
      },
    ];

    const measureCtx = document.createElement('canvas').getContext('2d');
    if (!measureCtx) {
      return '';
    }

    const fontFor = (size: number, bold: boolean, italic: boolean) =>
      `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${size}px ${SignService._signatureFont}`;

    interface LineSegment {
      text: string;
      width: number;
      font: string;
      underline: boolean;
    }
    interface LayoutLine {
      segments: LineSegment[];
      height: number;
      indent: number;
      align: 'left' | 'center' | 'right';
      marker: string | null;
      markerFont: string;
      markerX: number;
    }

    // Pre-pass: tokenize each block into words and measure its natural width so
    // the card can fit the longest line, clamped to the participant card range.
    interface BlockLayout {
      words: LineSegment[][];
      spaceWidth: number;
      indent: number;
      lineHeight: number;
      markerFont: string;
      align: 'left' | 'center' | 'right';
      marker: string | null;
    }

    const blockLayouts: BlockLayout[] = [];
    let longest = 0;

    for (const block of blocks) {
      const lineHeight = Math.round(block.fontSize * 1.4);
      const indent = block.indent + (block.listMarker ? markerGap : 0);

      measureCtx.font = fontFor(block.fontSize, false, false);
      const spaceWidth = measureCtx.measureText(' ').width;
      const markerFont = fontFor(block.fontSize, false, false);

      // Build words; a word is a list of styled segments so mid-word style
      // changes (e.g. "he<strong>ll</strong>o") stay glued together.
      const words: LineSegment[][] = [];
      let currentWord: LineSegment[] = [];
      const flushWord = () => {
        if (currentWord.length) {
          words.push(currentWord);
          currentWord = [];
        }
      };
      for (const run of block.runs) {
        const runFont = fontFor(block.fontSize, run.bold, run.italic);
        measureCtx.font = runFont;
        for (const part of run.text.split(/(\s+)/)) {
          if (part === '') {
            continue;
          }
          if (/^\s+$/.test(part)) {
            flushWord();
            continue;
          }
          currentWord.push({
            text: part,
            width: measureCtx.measureText(part).width,
            font: runFont,
            underline: run.underline,
          });
        }
      }
      flushWord();

      const natural =
        indent +
        words.reduce((sum, word) => sum + word.reduce((s, seg) => s + seg.width, 0), 0) +
        spaceWidth * Math.max(0, words.length - 1);
      longest = Math.max(longest, natural);

      blockLayouts.push({
        words,
        spaceWidth,
        indent,
        lineHeight,
        markerFont,
        align: block.align,
        marker: block.listMarker,
      });
    }

    const maxTextWidth = Math.min(maxWidth - padding * 2, Math.max(minWidth - padding * 2, Math.ceil(longest)));
    const width = maxTextWidth + padding * 2;

    // Wrap each block to the chosen width.
    const lines: LayoutLine[] = [];
    for (const bl of blockLayouts) {
      const avail = maxTextWidth - bl.indent;
      let lineWords: LineSegment[][] = [];
      let lineWidth = 0;
      let firstLine = true;
      const emitLine = () => {
        const segments: LineSegment[] = [];
        lineWords.forEach((word, wi) => {
          segments.push(...word);
          if (wi < lineWords.length - 1) {
            segments.push({ text: ' ', width: bl.spaceWidth, font: bl.markerFont, underline: false });
          }
        });
        lines.push({
          segments,
          height: bl.lineHeight,
          indent: bl.indent,
          align: bl.align,
          marker: firstLine ? bl.marker : null,
          markerFont: bl.markerFont,
          markerX: padding + bl.indent - markerGap + 2,
        });
        firstLine = false;
        lineWords = [];
        lineWidth = 0;
      };

      for (const word of bl.words) {
        const wordWidth = word.reduce((sum, seg) => sum + seg.width, 0);
        const addWidth = (lineWords.length > 0 ? bl.spaceWidth : 0) + wordWidth;
        if (lineWidth + addWidth > avail && lineWords.length > 0) {
          emitLine();
        }
        lineWords.push(word);
        lineWidth += addWidth;
      }
      emitLine();
    }

    const totalHeight = padding * 2 + lines.reduce((sum, line) => sum + line.height, 0);

    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = totalHeight * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return '';
    }
    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, totalHeight);
    ctx.fillStyle = '#000000';
    ctx.strokeStyle = '#000000';
    ctx.textBaseline = 'top';

    let y = padding;
    for (const line of lines) {
      const lineWidth = line.segments.reduce((sum, seg) => sum + seg.width, 0);
      const availLine = maxTextWidth - line.indent;
      let x = padding + line.indent;
      if (line.align === 'center') {
        x += Math.max(0, (availLine - lineWidth) / 2);
      } else if (line.align === 'right') {
        x += Math.max(0, availLine - lineWidth);
      }

      if (line.marker) {
        ctx.font = line.markerFont;
        ctx.fillText(line.marker, line.markerX, y);
      }

      for (const seg of line.segments) {
        ctx.font = seg.font;
        ctx.fillText(seg.text, x, y);
        if (seg.underline && seg.text.trim() !== '') {
          const underlineY = y + line.height - 4;
          ctx.beginPath();
          ctx.moveTo(x, underlineY);
          ctx.lineTo(x + seg.width, underlineY);
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        x += seg.width;
      }
      y += line.height;
    }

    return canvas.toDataURL('image/png');
  }

  private _parseDisclaimerBlocks(html: string, baseSize: number): SignatureBlock[] {
    if (!html || !html.trim()) {
      return [];
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks: SignatureBlock[] = [];

    const alignOf = (el: Element): 'left' | 'center' | 'right' => {
      if (el.classList.contains('ql-align-center')) {
        return 'center';
      }
      if (el.classList.contains('ql-align-right')) {
        return 'right';
      }
      return 'left';
    };

    const sizeOf = (tag: string): number => {
      switch (tag) {
      case 'H1':
        return Math.round(baseSize * 1.7);
      case 'H2':
        return Math.round(baseSize * 1.4);
      case 'H3':
        return Math.round(baseSize * 1.2);
      default:
        return baseSize;
      }
    };

    const collectNode = (node: Node, bold: boolean, italic: boolean, underline: boolean, out: SignatureRun[]) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (text) {
          out.push({ text, bold, italic, underline });
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const el = node as Element;
      const tag = el.tagName;
      el.childNodes.forEach(child =>
        collectNode(
          child,
          bold || tag === 'STRONG' || tag === 'B',
          italic || tag === 'EM' || tag === 'I',
          underline || tag === 'U',
          out
        )
      );
    };

    const pushBlock = (el: Element, listMarker: string | null, indent: number) => {
      const runs: SignatureRun[] = [];
      el.childNodes.forEach(child => collectNode(child, false, false, false, runs));
      blocks.push({
        runs: runs.length ? runs : [{ text: '', bold: false, italic: false, underline: false }],
        align: alignOf(el),
        fontSize: sizeOf(el.tagName),
        listMarker,
        indent,
      });
    };

    // Buffer bare text / inline nodes that sit directly under <body>: plain-text
    // disclaimers (e.g. Walkdown signatures) aren't always wrapped in a block element.
    let inlineBuffer: Node[] = [];
    const flushInlineBuffer = () => {
      if (!inlineBuffer.length) {
        return;
      }
      const runs: SignatureRun[] = [];
      inlineBuffer.forEach(node => collectNode(node, false, false, false, runs));
      inlineBuffer = [];
      if (runs.some(run => run.text.trim() !== '')) {
        blocks.push({ runs, align: 'left', fontSize: baseSize, listMarker: null, indent: 0 });
      }
    };

    const isBlockTag = (tag: string) =>
      tag === 'P' || tag === 'DIV' || tag === 'BLOCKQUOTE' || tag === 'PRE' || /^H[1-6]$/.test(tag);

    doc.body.childNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        const tag = el.tagName;
        if (tag === 'UL' || tag === 'OL') {
          flushInlineBuffer();
          let index = 1;
          el.childNodes.forEach(child => {
            if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'LI') {
              const marker = tag === 'OL' ? `${index}.` : '•';
              pushBlock(child as Element, marker, 20);
              index++;
            }
          });
          return;
        }
        if (isBlockTag(tag)) {
          flushInlineBuffer();
          pushBlock(el, null, 0);
          return;
        }
        inlineBuffer.push(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        inlineBuffer.push(node);
      }
    });
    flushInlineBuffer();

    return blocks;
  }

  unsignReponse(signature: Dtos.ResponseItemImage, actionItemId: number) {
    return this._responseService
      .uploadImage({
        ...signature,
        IsDeleted: true,
      })
      .pipe(switchMap(() => this._responseService.deleteResponseItem(actionItemId)));
  }
}
